import React, { useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { formatCurrency } from '../../utils/formatters';
import { sumAccountsBRL } from '../../utils/exchange';

function computeScore(state) {
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  const curTx = state.transactions.filter(t => t.date.startsWith(currentMonth) && t.status === 'paid' && !t.cardId);
  const curIncome = curTx.filter(t => t.type === 'income').reduce((s, t) => s + t.value, 0);
  const curExpense = curTx.filter(t => t.type === 'expense').reduce((s, t) => s + t.value, 0);

  // Average monthly expense from last 3 months
  let avgMonthlyExpense = 0;
  for (let i = 1; i <= 3; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const m = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    avgMonthlyExpense += state.transactions
      .filter(t => t.date.startsWith(m) && t.type === 'expense' && t.status === 'paid' && !t.cardId)
      .reduce((s, t) => s + t.value, 0);
  }
  avgMonthlyExpense /= 3;
  if (avgMonthlyExpense === 0) avgMonthlyExpense = curExpense;

  const totalBalance = sumAccountsBRL(state.accounts, state.usdRate);

  let score = 0;
  const details = [];

  // 1. Taxa de poupança (30 pts)
  const savingsRate = curIncome > 0 ? (curIncome - curExpense) / curIncome : -1;
  let savingsScore = savingsRate >= 0.2 ? 30 : savingsRate >= 0.1 ? 20 : savingsRate >= 0 ? 10 : 0;
  score += savingsScore;
  details.push({
    label: 'Taxa de poupança',
    score: savingsScore, max: 30,
    desc: curIncome > 0
      ? savingsRate >= 0 ? `${Math.round(savingsRate * 100)}% da renda` : 'Gastos maiores que receitas'
      : 'Nenhuma receita registrada',
  });

  // 2. Reserva de emergência (30 pts)
  const months_coverage = avgMonthlyExpense > 0 ? totalBalance / avgMonthlyExpense : 0;
  const emergencyScore = months_coverage >= 6 ? 30 : months_coverage >= 3 ? 20 : months_coverage >= 1 ? 10 : 0;
  score += emergencyScore;
  details.push({
    label: 'Reserva de emergência',
    score: emergencyScore, max: 30,
    desc: months_coverage > 0 ? `${months_coverage.toFixed(1)} meses de cobertura` : 'Saldo insuficiente',
  });

  // 3. Controle de orçamento (20 pts)
  let budgetScore = 10;
  if (state.budgets.length > 0) {
    const catMap = {};
    curTx.filter(t => t.type === 'expense').forEach(t => { catMap[t.categoryId] = (catMap[t.categoryId] || 0) + t.value; });
    const within = state.budgets.filter(b => (catMap[b.categoryId] || 0) <= b.planned).length;
    budgetScore = Math.round((within / state.budgets.length) * 20);
  }
  score += budgetScore;
  details.push({
    label: 'Controle de orçamento',
    score: budgetScore, max: 20,
    desc: state.budgets.length === 0 ? 'Configure orçamentos por categoria' : `${budgetScore >= 16 ? 'Dentro' : 'Fora'} do orçamento`,
  });

  // 4. Contas em dia (20 pts)
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const overdue = state.transactions.filter(
    t => t.status === 'pending' && t.type === 'expense' && new Date(t.date + 'T00:00:00') < today
  ).length;
  const overdueScore = overdue === 0 ? 20 : overdue <= 2 ? 10 : 0;
  score += overdueScore;
  details.push({
    label: 'Contas em dia',
    score: overdueScore, max: 20,
    desc: overdue === 0 ? 'Nenhum atraso' : `${overdue} conta${overdue > 1 ? 's' : ''} em atraso`,
  });

  const grade = score >= 85 ? 'A' : score >= 70 ? 'B' : score >= 50 ? 'C' : score >= 30 ? 'D' : 'F';
  const gradeColor = score >= 85 ? '#1d9e75' : score >= 70 ? '#3b82f6' : score >= 50 ? '#f59e0b' : score >= 30 ? '#f97316' : '#e24b4a';
  const gradeBg   = score >= 85 ? '#d1fae5' : score >= 70 ? '#dbeafe' : score >= 50 ? '#fef3c7' : score >= 30 ? '#ffedd5' : '#fee2e2';
  const label     = score >= 85 ? 'Excelente' : score >= 70 ? 'Boa' : score >= 50 ? 'Regular' : score >= 30 ? 'Atenção' : 'Crítica';

  return { score, grade, gradeColor, gradeBg, label, details };
}

export default function FinancialHealth() {
  const { state } = useApp();
  const { score, grade, gradeColor, gradeBg, label, details } = useMemo(() => computeScore(state), [state]);

  const R = 36;
  const circumference = 2 * Math.PI * R;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="card" style={{ padding: '20px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
        <div style={{ width: 34, height: 34, borderRadius: 10, background: gradeBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <i className="fas fa-heart-pulse" style={{ color: gradeColor, fontSize: 15 }}></i>
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Saúde Financeira</div>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>Diagnóstico do mês atual</div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 16 }}>
        {/* Gauge */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <svg width={88} height={88} viewBox="0 0 88 88">
            <circle cx={44} cy={44} r={R} fill="none" stroke="var(--surface2)" strokeWidth={8} />
            <circle cx={44} cy={44} r={R} fill="none" stroke={gradeColor} strokeWidth={8}
              strokeDasharray={circumference} strokeDashoffset={offset}
              strokeLinecap="round" transform="rotate(-90 44 44)"
              style={{ transition: 'stroke-dashoffset 1s ease' }}
            />
          </svg>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: gradeColor, lineHeight: 1 }}>{score}</div>
            <div style={{ fontSize: 9, color: 'var(--muted)', fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>pts</div>
          </div>
        </div>

        {/* Grade */}
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: gradeBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 20, color: gradeColor }}>{grade}</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: gradeColor }}>{label}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>Nota geral do mês</div>
            </div>
          </div>
          <div style={{ height: 6, background: 'var(--surface2)', borderRadius: 99, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${score}%`, background: `linear-gradient(90deg,${gradeColor}88,${gradeColor})`, borderRadius: 99, transition: 'width 1s ease' }} />
          </div>
        </div>
      </div>

      {/* Details */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {details.map(d => {
          const ok = d.score >= d.max * 0.75;
          const mid = d.score >= d.max * 0.4;
          const ic = ok ? 'fa-check' : mid ? 'fa-exclamation' : 'fa-xmark';
          const cl = ok ? '#1d9e75' : mid ? '#f59e0b' : '#e24b4a';
          const bg = ok ? '#d1fae5' : mid ? '#fef3c7' : '#fee2e2';
          return (
            <div key={d.label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 28, height: 28, borderRadius: 7, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className={`fas ${ic}`} style={{ fontSize: 10, color: cl }}></i>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontSize: 12, fontWeight: 600 }}>{d.label}</span>
                  <span style={{ fontSize: 11, color: 'var(--muted)' }}>{d.score}/{d.max}</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 1 }}>{d.desc}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
